import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  Box,
  Menu,
  FormControlLabel,
  Checkbox,
  TextField,
  Typography,
  Card,
  CardContent,
  Breadcrumbs,
  Stack,
  CircularProgress,
  Alert,
  Fade,
  Paper,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  TableSortLabel,
  OutlinedInput,
  Divider,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import { Link, useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import NavBar from "../NavBar";
import api from "../../axiosInstance";
import { useTheme } from "@emotion/react";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import { toast, ToastContainer } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import BankDetailsModal from "./BankDetailsModal";
import MentorConfigModal from "./MentorConfigModal";
import MentorDocumentsUploads from "./MentorDocumentsUploads";
import MentorRegisterForm from "./MentorRegisterForm";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SettingsIcon from "@mui/icons-material/Settings";
import AssuredWorkloadIcon from "@mui/icons-material/AssuredWorkload";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import DescriptionIcon from "@mui/icons-material/Description";
import SchoolIcon from "@mui/icons-material/School";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import BadgeIcon from "@mui/icons-material/Badge";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import HowToRegIcon from "@mui/icons-material/HowToReg";

const getDocIcon = (type) => {
  switch (type) {
    case "AADHAAR":
      return <BadgeIcon sx={{ fontSize: 16 }} />;

    case "QUALIFICATION":
      return <SchoolIcon sx={{ fontSize: 16 }} />;

    case "EXPERIENCE_PROOF":
      return <WorkHistoryIcon sx={{ fontSize: 16 }} />;

    case "BANK_PROOF":
      return <AccountBalanceIcon sx={{ fontSize: 16 }} />;

    default:
      return <DescriptionIcon sx={{ fontSize: 16 }} />;
  }
};

// Resolve a document path/URL to an absolute URL (mirrors the profile image logic)
const resolveDocUrl = (docUrl) => {
  if (!docUrl) return "";
  return /^https?:\/\//.test(docUrl)
    ? docUrl
    : `https://siaedutech.com/public/${docUrl}`;
};

// A document is considered verified when the backend reports it APPROVED
// (the review API returns "APPROVED"); "VERIFIED" is accepted for older data.
const isDocApproved = (status) =>
  status === "APPROVED" || status === "VERIFIED";

// Document chips. When onDocClick is provided the chip becomes clickable and
// opens the document preview popup.
const DocumentChips = ({ documents, onDocClick }) => (
  <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      gap: "6px",
      justifyContent: "left",
    }}
  >
    {documents?.map((doc) => (
      <div
        key={doc.id}
        onClick={onDocClick ? () => onDocClick(doc) : undefined}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 10px",
          borderRadius: "14px",
          backgroundColor: isDocApproved(doc.status) ? "#E8F5E9" : "#FFF3F3",
          border: `1px solid ${
            isDocApproved(doc.status) ? "#81C784" : "#EF9A9A"
          }`,
          fontSize: "12px",
          fontWeight: 500,
          color: "#424242",
          whiteSpace: "nowrap",
          cursor: onDocClick ? "pointer" : "default",
          transition: "0.2s",
        }}
        onMouseEnter={(e) => {
          if (onDocClick) e.currentTarget.style.transform = "scale(1.03)";
        }}
        onMouseLeave={(e) => {
          if (onDocClick) e.currentTarget.style.transform = "scale(1)";
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            color: isDocApproved(doc.status) ? "#2E7D32" : "#D32F2F",
          }}
        >
          {getDocIcon(doc.docType)}
        </span>

        <span>
          {(doc.docType || "")
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase())}
        </span>

        {isDocApproved(doc.status) ? (
          <CheckCircleIcon sx={{ fontSize: 16, color: "#2E7D32" }} />
        ) : (
          <CancelIcon sx={{ fontSize: 16, color: "#D32F2F" }} />
        )}
      </div>
    ))}
  </div>
);

// Demo video chips. When onVideoClick is provided the chip opens the video
// preview popup instead of a new browser tab.
const DemoVideoChips = ({ demoVideos, onVideoClick }) => (
  <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      gap: "6px",
      justifyContent: "left",
    }}
  >
    {demoVideos?.map((video) => (
      <div
        key={video.id}
        onClick={onVideoClick ? () => onVideoClick(video) : undefined}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "5px 10px",
          borderRadius: "14px",
          backgroundColor: video.status === "APPROVED" ? "#E8F5E9" : "#FFF3F3",
          border: `1px solid ${
            video.status === "APPROVED" ? "#81C784" : "#EF9A9A"
          }`,
          cursor: onVideoClick ? "pointer" : "default",
          fontSize: "12px",
          fontWeight: 500,
          whiteSpace: "nowrap",
          transition: "0.2s",
        }}
        onMouseEnter={(e) => {
          if (onVideoClick) e.currentTarget.style.transform = "scale(1.03)";
        }}
        onMouseLeave={(e) => {
          if (onVideoClick) e.currentTarget.style.transform = "scale(1)";
        }}
      >
        <PlayCircleFilledIcon
          sx={{
            fontSize: 18,
            color: video.status === "APPROVED" ? "#2E7D32" : "#D32F2F",
          }}
        />

        <span>{video.subjectName}</span>

        {video.status === "APPROVED" ? (
          <CheckCircleIcon sx={{ fontSize: 16, color: "#2E7D32" }} />
        ) : (
          <CancelIcon sx={{ fontSize: 16, color: "#D32F2F" }} />
        )}
      </div>
    ))}
  </div>
);

// Column config and visibility state
const allColumns = [
  {
    key: "id",
    label: "ID",
    cellCss: { align: "center", minWidth: "50px" },
    getValue: (r) => r.profile?.id,
  },
  {
    key: "mentorId",
    label: "User ID",
    cellCss: { align: "center", minWidth: "100px" },
    getValue: (r) => r.profile?.mentorId,
  },
  {
    key: "username",
    label: "Full Name",
    cellCss: { align: "center", minWidth: "150px" },
    getValue: (r) => r.profile?.username,
  },
  {
    key: "highestQualification",
    label: "Highest Qualification",
    cellCss: { align: "center", minWidth: "200px" },
    getValue: (r) => r.profile?.highestQualification,
  },
  {
    key: "experienceYears",
    label: "Experience (Years)",
    cellCss: { align: "center", minWidth: "150px" },
    getValue: (r) => r.profile?.experienceYears,
  },
  {
    key: "bio",
    label: "Bio",
    cellCss: { align: "center", minWidth: "200px" },
    getValue: (r) => r.profile?.bio,
  },
  {
    key: "currencyCode",
    label: "Currency Code",
    cellCss: { align: "center", minWidth: "150px" },
    getValue: (r) => r.profile?.currencyCode,
  },
  {
    key: "university",
    label: "Universities",
    cellCss: { align: "center", minWidth: "150px" },
    getValue: (r) => r.universities?.map((u) => u.name).join(", ") || "-",
  },
  {
    key: "courses",
    label: "Courses",
    cellCss: { align: "center", minWidth: "250px" },
    getValue: (r) =>
      r.subjects
        ?.flatMap((subject) => subject.courses || [])
        .map((course) => course.courseName)
        .join(", ") || "-",
  },
  {
    key: "subjects",
    label: "Subjects",
    cellCss: { align: "center", minWidth: "180px" },
    getValue: (r) =>
      r.subjects?.map((subject) => subject.subjectName).join(", ") || "-",
  },
  {
    key: "languages",
    label: "Languages",
    cellCss: { align: "center", minWidth: "150px" },

    getValue: (r) => r.languages?.map((u) => u.name).join(", ") || "-",
  },
  {
    key: "profileImageUrl",
    label: "Profile Image",
    cellCss: { align: "center", minWidth: "150px" },

    getValue: (r) => r.profile?.profileImageUrl || "-",
  },
  {
    key: "phoneNumber",
    label: "Phone Number",
    cellCss: { align: "center", minWidth: "150px" },
    getValue: (r) => r.profile?.phoneNumber || "-",
  },
  {
    key: "documents",
    label: "Documents",
    cellCss: { align: "left", minWidth: "350px" },
    getValue: (r) => <DocumentChips documents={r.documents} />,
  },
  {
    key: "demoVideos",
    label: "Demo Videos",
    cellCss: { align: "left", minWidth: "350px" },
    getValue: (r) => <DemoVideoChips demoVideos={r.demoVideos} />,
  },
  // {
  //   key: "status",
  //   label: "Status",
  //   cellCss: { align: "center", minWidth: "120px" },
  //   getValue: (r) => r.status,
  // },
];
const defaultVisibleKeys = new Set([
  "id",
  "mentorId",
  "username",
  "highestQualification",
  "experienceYears",
  "bio",
  "currencyCode",
  "university",
  "documents",
  "demoVideos",
  "phoneNumber",
  // "mentoringMode",
  // "active",
]);

const STORAGE_KEY = "MentorsVisibleColumns";

// Mentor profile lifecycle: PENDING -> APPROVED -> SUSPENDED -> REINSTATE (back to APPROVED);
// PENDING/REJECTED -> APPROVE. Drives both the trigger icon/color and the
// action menu shown for each row.
const mentorStatusCfg = {
  PENDING: { label: "Pending", color: "#ed6c02", Icon: PersonSearchIcon },
  APPROVED: { label: "Approved", color: "#2e7d32", Icon: PersonAddIcon },
  REJECTED: { label: "Rejected", color: "#d32f2f", Icon: PersonRemoveIcon },
  SUSPENDED: {
    label: "Suspended",
    color: "#6a1b9a",
    Icon: PersonOffIcon,
  },
};

// Actions available from each current status, in menu order.
const mentorActionsByStatus = {
  PENDING: [
    {
      action: "APPROVE",
      label: "Approve",
      color: "#2e7d32",
      Icon: PersonAddIcon,
    },
    { action: "REJECT", label: "Reject", color: "#d32f2f", Icon: PersonRemoveIcon },
  ],
  APPROVED: [
    {
      action: "SUSPEND",
      label: "Suspend",
      color: "#6a1b9a",
      Icon: PersonOffIcon,
    },
  ],
  REJECTED: [
    {
      action: "APPROVE",
      label: "Approve",
      color: "#2e7d32",
      Icon: PersonAddIcon,
    },
  ],
  SUSPENDED: [
    {
      action: "REINSTATE",
      label: "Reinstate",
      color: "#1565c0",
      Icon: HowToRegIcon,
    },
  ],
};

function MentorsTable() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [colleges, setColleges] = useState([]);
  const [mentorshipDetails, setMentorshipDetails] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const handleNewClose = () => setOpenNew(false);
  const [filterSubjectCategory, setFilterSubjectCategory] = useState("");
  const [filterSubject, setFilterSubject] = useState("");
  const [mentorName, setMentorName] = useState(() => {
    return localStorage.getItem("mentor_mangement") || "";
  });
  const [coreSubjectList, setCoreSubjectList] = useState([]);
  const [groupedSubjectList, setGroupedSubjectList] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [openImage, setOpenImage] = useState(null);
  const [openDoc, setOpenDoc] = useState(null);
  const [approvingDoc, setApprovingDoc] = useState(false);
  const [docReviewNote, setDocReviewNote] = useState("");
  const [openVideo, setOpenVideo] = useState(null);
  const [approvingVideo, setApprovingVideo] = useState(false);
  const [videoReviewNote, setVideoReviewNote] = useState("");
  const [mentorshipDetailsEdit, setMentorshipDetailsEdit] = useState(null);
  const [dense, setDense] = useState(true);
  const [orderBy, setOrderBy] = useState("category");
  const [order, setOrder] = useState("asc");
  const [totalElements, setTotalElements] = useState(0);
  const [bankViewRow, setBankViewRow] = useState(null);
  const [openBankView, setOpenBankView] = useState(false);
  const [openBankEdit, setOpenBankEdit] = useState(false);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [statusMenuRow, setStatusMenuRow] = useState(null);
  const [openConfig, setOpenConfig] = useState(false);
  const [bankEditData, setBankEditData] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);
  const [bankAccountToDelete, setBankAccountToDelete] = useState(null);
  const [openRegisterForm, setOpenRegisterForm] = useState(false);
  const [registerFormMentor, setRegisterFormMentor] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  console.log(mentorshipDetails);

  // Inside sorted Data your component
  const filteredData = useMemo(() => {
    if (!Array.isArray(mentorshipDetails)) return [];

    const normalizedSearch = mentorName.trim().toLowerCase();
    return mentorshipDetails.filter((row) => {
      const textMatch =
        !normalizedSearch ||
        [
          row.profile?.username,
          row.username,
          row.profile?.firstName,
          row.profile?.lastName,
          row.name,
          row.profile?.mentorId?.toString(),
          row.highestQualification,
          row.qualification,
          row.university,
          row.industry,
          row.bio,
          row.status,
          row.profile?.status,
        ]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(normalizedSearch));

      const subjectMatch =
        !filterSubjectCategory ||
        row.subjects?.some((subject) =>
          [subject.subjectCategory, subject.subjectName]
            .filter(Boolean)
            .some((value) =>
              value.toLowerCase().includes(filterSubjectCategory.toLowerCase()),
            ),
        );

      const subjectNameMatch =
        !filterSubject ||
        row.subjects?.some((subject) =>
          [subject.subjectName, subject.subjectCategory]
            .filter(Boolean)
            .some((value) =>
              value.toLowerCase().includes(filterSubject.toLowerCase()),
            ),
        );

      return textMatch && subjectMatch && subjectNameMatch;
    });
  }, [mentorshipDetails, mentorName, filterSubjectCategory, filterSubject]);

  const sortedData = useMemo(() => {
    if (!filteredData) return [];

    const col = allColumns.find((c) => c.key === orderBy);
    // Not a sortable column (or column renders JSX) -> keep original order
    if (!col) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = col.getValue(a);
      const bValue = col.getValue(b);

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === "string") {
        return order === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }, [filteredData, orderBy, order]);

  // Sorting logic
  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  // padding Dense
  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };

  //Fetching the University data with mentorshipDetails id
  useEffect(() => {
    setIsLoading(true);
    api
      .get(`/admin/mentors`, {
        params: {
          page: page,
          size: rowsPerPage,
        },
      })
      .then((res) => {
        const mentors = res.data.data.content || [];
        setMentorshipDetails(mentors);
        setTotalElements(res.data.data.totalElements ?? 0);
      })

      .catch((error) => {
        console.error("There was an error fetching the book details!", error);
        setError("Failed to load Mentors Details. Please try again.");
      })
      .finally(() => setIsLoading(false));
  }, [page, rowsPerPage, refreshKey]);

  // Core Subjects for the filter dropdown (real endpoint)
  useEffect(() => {
    api
      .get("/mentor-catalog/coreSubjects")
      .then((res) => setCoreSubjectList(res.data.data ?? []))
      .catch((err) => console.error("Core subjects fetch error:", err));
  }, []);

  // Grouped Subjects for the selected Core Subject (real endpoint, cascading).
  // filterSubjectCategory holds the category's NAME (to match the existing
  // name-based mentor filtering below), so resolve its id before calling the
  // API, which expects core_subject_id.
  useEffect(() => {
    const selectedCategory = coreSubjectList.find(
      (cat) => cat.subjectName === filterSubjectCategory,
    );
    if (!selectedCategory) {
      setGroupedSubjectList([]);
      return;
    }
    api
      .get("/mentor-catalog/groupedSubjects", {
        params: { core_subject_id: selectedCategory.id },
      })
      .then((res) => setGroupedSubjectList(res.data.data ?? []))
      .catch((err) => console.error("Grouped subjects fetch error:", err));
  }, [filterSubjectCategory, coreSubjectList]);

  // Initialize from localStorage synchronously to avoid a race with the persister effect
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          const validKeys = new Set(allColumns.map((c) => c.key));
          const filtered = arr.filter((k) => validKeys.has(k));
          if (filtered.length > 0) {
            return new Set(filtered);
          }
        }
      }
    } catch {}
    return new Set(Array.from(defaultVisibleKeys));
  });

  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  // Persist column config to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(Array.from(visibleColumns)),
      );
    } catch {}
  }, [visibleColumns]);

  const handleOpenColumnsMenu = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumnsMenu = () => setColumnsMenuAnchor(null);
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Helper function to get boolean chip styling
  const getBooleanChip = (value) => {
    const normalized = value === true || value === "true" || value === "Yes";
    return (
      <Chip
        label={normalized ? "Yes" : "No"}
        color={normalized ? "success" : "error"}
        size="small"
        variant="outlined"
      />
    );
  };

  // Helper function to get content type chip styling
  const getContentTypeChip = (type) => {
    const typeConfig = {
      text: { color: "primary", label: "Text" },
      image: { color: "secondary", label: "Image" },
      video: { color: "success", label: "Video" },
      link: { color: "info", label: "Link" },
    };

    const config = typeConfig[type?.toLowerCase()] || {
      color: "default",
      label: type || "Unknown",
    };
    return <Chip label={config.label} color={config.color} size="small" />;
  };
  //   Page nation handel changes
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // page nation rows handles
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };
  useEffect(() => {
    setPage(0);
  }, [mentorName, filterSubjectCategory, filterSubject]);

  // pagenation code
  // const sortedData = sortedData;

  // Columns Field showing obeject
  const visibleColumnDefs = allColumns.filter((c) => visibleColumns.has(c.key));
  // Deletion confirm dialog box open and close.
  const handleClickOpen = (id) => {
    setSelectedId(id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedId(null);
  };
  // Delete The banner record api fetch data
  const handleDelete = async () => {
    try {
      await api.delete(`/api/v1/mentorshipDetails/${selectedId}`);

      // If deletion is successful, update local state update
      setMentorshipDetails((prev) =>
        prev.filter((row) => row.id !== selectedId),
      );
      toast.success("Record deleted successfully.!", {
        position: "top-right",
        autoClose: 3000,
      });
      setOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete the record. Please try again.");
    }
  };
  const handleOpenStatusMenu = (e, row) => {
    setStatusMenuAnchor(e.currentTarget);
    setStatusMenuRow(row);
  };
  const handleCloseStatusMenu = () => {
    setStatusMenuAnchor(null);
    setStatusMenuRow(null);
  };

  const fallbackStatusByAction = {
    APPROVE: "APPROVED",
    REJECT: "REJECTED",
    SUSPEND: "SUSPENDED",
    REINSTATE: "APPROVED",
  };

  const handleMentorDecision = async (row, action, reason = "") => {
    const ApprovedID = row.profile?.id;
    try {
      const res = await api.post(`/admin/mentors/${ApprovedID}/decision`, {
        action,
        reason,
      });
      const newStatus =
        res?.data?.data?.status ||
        res?.data?.status ||
        fallbackStatusByAction[action];
      setMentorshipDetails((prev) =>
        prev.map((item) =>
          item.profile?.id === ApprovedID
            ? { ...item, profile: { ...item.profile, status: newStatus } }
            : item,
        ),
      );
      toast.success(
        `Mentor ${mentorStatusCfg[newStatus]?.label.toLowerCase() || newStatus.toLowerCase()} successfully!`,
      );
    } catch (error) {
      toast.error(
        error.response?.data?.errorMessage ||
          "Failed to update status. Please try again.",
      );
    } finally {
      handleCloseStatusMenu();
      handleCloseReasonDialog();
    }
  };

  // Reject/Suspend require a reason — Approve/Reinstate fire immediately.
  const ACTIONS_REQUIRING_REASON = ["REJECT", "SUSPEND"];
  const [reasonDialogRow, setReasonDialogRow] = useState(null);
  const [reasonDialogAction, setReasonDialogAction] = useState(null);
  const [reasonText, setReasonText] = useState("");
  const [submittingReason, setSubmittingReason] = useState(false);

  const handleSelectMentorAction = (row, action) => {
    if (ACTIONS_REQUIRING_REASON.includes(action)) {
      setReasonDialogRow(row);
      setReasonDialogAction(action);
      setReasonText("");
      setStatusMenuAnchor(null);
      setStatusMenuRow(null);
      return;
    }
    handleMentorDecision(row, action);
  };

  const handleCloseReasonDialog = () => {
    setReasonDialogRow(null);
    setReasonDialogAction(null);
    setReasonText("");
    setSubmittingReason(false);
  };

  const handleConfirmReasonDialog = async () => {
    if (!reasonDialogRow || !reasonDialogAction || !reasonText.trim()) return;
    setSubmittingReason(true);
    await handleMentorDecision(
      reasonDialogRow,
      reasonDialogAction,
      reasonText.trim(),
    );
  };
  // Review a mentor document from the preview popup.
  // Toggles between VERIFIED and REJECTED. A review note is required to reject.
  const handleApproveDoc = async () => {
    if (!openDoc) return;
    const isVerified = isDocApproved(openDoc.status);
    const newStatus = isVerified ? "REJECTED" : "APPROVED";
    // Backend requires a review note when rejecting a document
    if (newStatus === "REJECTED" && !docReviewNote.trim()) {
      toast.error("Review note is required when rejecting a document.");
      return;
    }
    try {
      setApprovingDoc(true);
      const res = await api.post(
        `/admin/mentors/documents/${openDoc.id}/review`,
        {
          status: newStatus,
          reviewNote: docReviewNote.trim(),
        },
      );
      const updatedStatus =
        res?.data?.data?.status || res?.data?.status || newStatus;
      // Update the document status in the table data
      setMentorshipDetails((prev) =>
        prev.map((mentor) => ({
          ...mentor,
          documents: mentor.documents?.map((d) =>
            d.id === openDoc.id ? { ...d, status: updatedStatus } : d,
          ),
        })),
      );
      // Reflect the new status in the open popup
      setOpenDoc((prev) => (prev ? { ...prev, status: updatedStatus } : prev));
      setDocReviewNote("");
      toast.success(
        `Document ${
          isDocApproved(updatedStatus) ? "approved" : "rejected"
        } successfully!`,
      );
    } catch (error) {
      console.error("Review failed:", error);
      toast.error(
        error.response?.data?.errorMessage ||
          "Failed to update document. Please try again.",
      );
    } finally {
      setApprovingDoc(false);
    }
  };
  // Review a mentor demo video from the preview popup.
  // Toggles between APPROVED and REJECTED. Backend validates the review note.
  const handleApproveVideo = async () => {
    if (!openVideo) return;
    const isApproved = openVideo.status === "APPROVED";
    const newStatus = isApproved ? "REJECTED" : "APPROVED";
    try {
      setApprovingVideo(true);
      const res = await api.post(
        `/admin/mentors/videos/${openVideo.id}/review`,
        {
          status: newStatus,
          reviewNote: videoReviewNote.trim(),
        },
      );
      const updatedStatus =
        res?.data?.data?.status || res?.data?.status || newStatus;
      // Update the video status in the table data
      setMentorshipDetails((prev) =>
        prev.map((mentor) => ({
          ...mentor,
          demoVideos: mentor.demoVideos?.map((v) =>
            v.id === openVideo.id ? { ...v, status: updatedStatus } : v,
          ),
        })),
      );
      // Reflect the new status in the open popup
      setOpenVideo((prev) =>
        prev ? { ...prev, status: updatedStatus } : prev,
      );
      setVideoReviewNote("");
      toast.success(
        `Demo video ${
          updatedStatus === "APPROVED" ? "approved" : "rejected"
        } successfully!`,
      );
    } catch (error) {
      console.error("Review failed:", error);
      toast.error(
        error.response?.data?.errorMessage ||
          "Failed to update video. Please try again.",
      );
    } finally {
      setApprovingVideo(false);
    }
  };
  // opening Adding page data in onclick
  const handleNewOpen = () => {
    setOpenNew(true);
    setMentorshipDetailsEdit([]);
  };
  // Editing page data in onclick
  const handleEditClick = (rowData) => {
    handleNewOpen();
    setMentorshipDetailsEdit(rowData);
  };
  // Open the mentor profile edit form
  const handleEditProfileClick = (rowData) => {
    setRegisterFormMentor(rowData);
    setOpenRegisterForm(true);
  };
  const handleRegisterFormSuccess = () => {
    setOpenRegisterForm(false);
    setRegisterFormMentor(null);
    setRefreshKey((k) => k + 1);
  };

  // Load the bank accounts for whichever mentor is currently in view.
  const fetchBankAccounts = async (mentorProfileId) => {
    if (!mentorProfileId) return;
    setLoadingBankAccounts(true);
    try {
      const res = await api.get(
        `/admin/mentors/${mentorProfileId}/bank-accounts`,
      );
      setBankAccounts(res.data?.data || []);
    } catch (error) {
      toast.error("Failed to load bank accounts.");
      setBankAccounts([]);
    } finally {
      setLoadingBankAccounts(false);
    }
  };

  const handleViewBankDetails = (row) => {
    setBankViewRow(row);
    setOpenBankView(true);
    fetchBankAccounts(row.profile?.id);
  };

  const handleAddBankAccount = (row) => {
    setBankViewRow(row);
    setBankEditData(null);
    setOpenBankEdit(true);
  };

  const handleEditBankAccount = (account) => {
    setBankEditData(account);
    setOpenBankEdit(true);
  };

  const handleDeleteBankAccount = async () => {
    if (!bankAccountToDelete || !bankViewRow?.profile?.id) return;
    try {
      await api.delete(
        `/admin/mentors/${bankViewRow.profile.id}/bank-accounts/${bankAccountToDelete.id}`,
      );
      toast.success("Bank account deleted successfully!");
      setBankAccountToDelete(null);
      fetchBankAccounts(bankViewRow.profile.id);
    } catch (error) {
      toast.error(
        error.response?.data?.errorMessage ||
          "Failed to delete bank account. Please try again.",
      );
    }
  };
  // Success show th response values
  const handleFormSuccess = (newItem) => {
    if (!newItem || !newItem.id) {
      console.warn("Invalid item received from form", newItem);
      return;
    }
    setMentorshipDetails((prev) => {
      const exists = prev.some((item) => item.id === newItem.id);
      console.log(exists);
      if (exists) {
        return prev.map((item) => (item.id === newItem.id ? newItem : item));
      } else {
        return [newItem, ...prev];
      }
    });

    setMentorshipDetailsEdit(null);
  };
  return (
    <>
      {/* <NavBar /> */}
      <ToastContainer />
      {/* Search Section */}
      <Card sx={{ margin: "0 15px", boxShadow: 1 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flex: 1,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                label="Mentor Name"
                placeholder="Search by mentor name..."
                value={mentorName}
                onChange={(e) => setMentorName(e.target.value)}
                sx={{ minWidth: 300 }}
              />
              <FormControl size="small" sx={{ minWidth: 300, textAlign: "left" }}>
                <InputLabel>Core Subjects</InputLabel>
                <Select
                  label="Core Subjects"
                  value={filterSubjectCategory}
                  onChange={(e) => {
                    setFilterSubjectCategory(e.target.value);
                    setFilterSubject("");
                  }}
                  sx={{ "& .MuiSelect-select": { textAlign: "left" } }}
                >
                  <MenuItem value="">All</MenuItem>
                  {coreSubjectList.map((cat) => (
                    <MenuItem key={cat.id} value={cat.subjectName}>
                      {cat.subjectName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 300, textAlign: "left" }}>
                <InputLabel>Grouped Subjects</InputLabel>
                <Select
                  label="Grouped Subjects"
                  value={filterSubject}
                  onChange={(e) => setFilterSubject(e.target.value)}
                  disabled={!filterSubjectCategory}
                  sx={{ "& .MuiSelect-select": { textAlign: "left" } }}
                >
                  <MenuItem value="">All</MenuItem>
                  {groupedSubjectList.map((s) => (
                    <MenuItem key={s.id} value={s.groupSubjectTitle}>
                      {s.groupSubjectTitle}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography
                sx={{ display: { xs: "none", md: "block" }, textAlign: "left" }}
                variant="body2"
                color="text.secondary"
              >
                {sortedData.length} of {totalElements} Mentors
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<ViewColumnIcon />}
                  onClick={handleOpenColumnsMenu}
                  sx={{ fontSize: { xs: "10px", md: "12px" } }}
                >
                  Columns
                </Button>
                <Menu
                  anchorEl={columnsMenuAnchor}
                  open={openColumnsMenu}
                  onClose={handleCloseColumnsMenu}
                  PaperProps={{
                    sx: {
                      width: 200,
                      maxHeight: 500,
                      overflowY: "auto",
                      borderRadius: 2,
                    },
                  }}
                >
                  {allColumns.map((col) => (
                    <FormControlLabel
                      key={col.key}
                      control={
                        <Checkbox
                          checked={visibleColumns.has(col.key)}
                          onChange={() => toggleColumn(col.key)}
                        />
                      }
                      label={col.label}
                      sx={{ pl: 1, pr: 2 }}
                    />
                  ))}
                </Menu>
                {/* <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleNewOpen}
                  {...BUTTON_STYLES.primary}
                  sx={{ fontSize: { xs: "10px", md: "12px" } }}
                >
                  Add Mentors
                </Button> */}
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SettingsIcon />}
                  onClick={() => setOpenConfig(true)}
                  {...BUTTON_STYLES.primary}
                  sx={{ fontSize: { xs: "10px", md: "12px" } }}
                >
                  Mentor Config
                </Button>
              </Stack>
              <Tooltip title="Dense padding" arrow>
                <FormControlLabel
                  control={
                    <Switch
                      checked={dense}
                      onChange={handleChangeDense}
                      color="primary"
                    />
                  }
                />
              </Tooltip>
            </Box>
          </Box>
          <Typography
            sx={{ display: { xs: "block", md: "none" }, textAlign: "left" }}
            variant="body2"
            color="text.secondary"
          >
            {sortedData.length} of {totalElements} Mentors
          </Typography>
        </CardContent>
      </Card>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {!isLoading && (
        <Fade in={!isLoading}>
          <Paper
            sx={{
              width: "98%",
              mx: "auto",
              overflow: "hidden",
              boxShadow: 2,
              marginBottom: "20px",
            }}
          >
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table
                stickyHeader
                aria-label="news table"
                size={dense ? "small" : "medium"}
              >
                <TableHead>
                  <TableRow>
                    {visibleColumnDefs.map((c) => (
                      <TableCell
                        key={c.key}
                        sx={{
                          padding: "10px 5px ",
                          backgroundColor: "#17479e",
                          color: "white",
                          fontWeight: 600,
                          ...c.cellCss,
                        }}
                        sortDirection={orderBy === c.key ? order : false}
                      >
                        <TableSortLabel
                          active={orderBy === c.key}
                          direction={orderBy === c.key ? order : "asc"}
                          onClick={() => handleSort(c.key)}
                          sx={{
                            color: "#ffffff !important",
                            "&.Mui-active": {
                              color: "#ffffff !important",
                            },
                            "& .MuiTableSortLabel-icon": {
                              color: "#ffffff !important",
                            },
                          }}
                        >
                          {c.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        backgroundColor: "#17479e",
                        color: "white",
                        fontWeight: 600,
                        minWidth: 120,
                        textAlign: "center",
                        position: "sticky",
                        right: 0,
                        zIndex: 2,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {console.log(sortedData)}
                  {Array.isArray(sortedData) && sortedData.length > 0 ? (
                    sortedData.map((row) => (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={row.id}
                        sx={{
                          "&:nth-of-type(odd)": {
                            backgroundColor: "rgba(0, 0, 0, 0.02)",
                          },
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        {visibleColumnDefs.map((c) => (
                          <TableCell
                            key={c.key}
                            sx={{ padding: "10px 10px", ...c.cellCss }}
                          >
                            {(() => {
                              const value = c.getValue(row);
                              const key = c.key;

                              // Optional debug

                              if (key === "viewable" || key === "isNational") {
                                return getBooleanChip(value);
                              }

                              if (key === "contentType") {
                                return getContentTypeChip(value);
                              }

                              if (key === "documents") {
                                return (
                                  <DocumentChips
                                    documents={row.documents}
                                    onDocClick={(doc) => {
                                      setDocReviewNote("");
                                      setOpenDoc(doc);
                                    }}
                                  />
                                );
                              }

                              if (key === "demoVideos") {
                                return (
                                  <DemoVideoChips
                                    demoVideos={row.demoVideos}
                                    onVideoClick={(video) => {
                                      setVideoReviewNote("");
                                      setOpenVideo(video);
                                    }}
                                  />
                                );
                              }

                              if (key === "profileImageUrl") {
                                return key === "profileImageUrl" ? (
                                  <img
                                    src={`https://siaedutech.com/public/${value}`}
                                    alt="preview"
                                    style={{
                                      width: "60px",
                                      height: "auto",
                                      borderRadius: "4px",
                                      cursor: "pointer",
                                    }}
                                    onClick={() =>
                                      setOpenImage(
                                        `https://siaedutech.com/public/${value}`,
                                      )
                                    }
                                  />
                                ) : (
                                  value
                                );
                              }

                              return value;
                            })()}
                          </TableCell>
                        ))}
                        <TableCell
                          sx={{
                            align: "center",
                            padding: "10px 10px",
                            minWidth: "120px",
                            position: "sticky",
                            backgroundColor: "#ffffff",
                            right: 0,
                            zIndex: 1,
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="center"
                          >
                            {(() => {
                              const statusKey = (
                                row.profile?.status || "PENDING"
                              ).toUpperCase();
                              const cfg =
                                mentorStatusCfg[statusKey] ??
                                mentorStatusCfg.PENDING;
                              const StatusIcon = cfg.Icon;
                              return (
                                <Tooltip
                                  title={`${cfg.label} — click for actions`}
                                  arrow
                                  placement="top"
                                >
                                  <IconButton
                                    size="small"
                                    onClick={(e) =>
                                      handleOpenStatusMenu(e, row)
                                    }
                                    sx={{
                                      color: cfg.color,
                                      "&:hover": {
                                        backgroundColor: cfg.color,
                                        color: "white",
                                      },
                                    }}
                                  >
                                    <StatusIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              );
                            })()}
                            <Tooltip title="Edit Profile" arrow placement="top">
                              <IconButton
                                color="primary"
                                onClick={() => handleEditProfileClick(row)}
                                size="small"
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "primary.light",
                                    color: "white",
                                  },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title="Upload mentor documents"
                              arrow
                              placement="top"
                            >
                              <IconButton
                                color="primary"
                                onClick={() => handleEditClick(row)}
                                size="small"
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "primary.light",
                                    color: "white",
                                  },
                                }}
                              >
                                <UploadFileIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title="Add Bank Details"
                              arrow
                              placement="top"
                            >
                              <IconButton
                                color="primary"
                                onClick={() => handleAddBankAccount(row)}
                                size="small"
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "primary.light",
                                    color: "white",
                                  },
                                }}
                              >
                                <AccountBalanceIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title="View Bank Details"
                              arrow
                              placement="top"
                            >
                              <IconButton
                                color="primary"
                                onClick={() => handleViewBankDetails(row)}
                                size="small"
                                sx={{
                                  color: "#28b704",
                                  "&:hover": {
                                    backgroundColor: "#28b704",
                                    color: "white",
                                  },
                                }}
                              >
                                <AssuredWorkloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {/* <Tooltip
                              title="Delete Mentor"
                              arrow
                              placement="top"
                            >
                              <IconButton
                                color="error"
                                onClick={() => handleClickOpen(row.id)}
                                size="small"
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "error.light",
                                    color: "white",
                                  },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip> */}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={visibleColumnDefs.length + 1}
                        sx={{ textAlign: "center", py: 4 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Typography variant="h6" color="text.secondary">
                            {mentorName
                              ? "No mentorshipDetails found matching your search"
                              : "No mentorshipDetails available"}
                          </Typography>
                          {mentorName && (
                            <Button
                              variant="outlined"
                              onClick={() => setMentorName("")}
                              startIcon={<SearchIcon />}
                            >
                              Clear Search
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 30, 50, 100]}
              component="div"
              count={Number(totalElements) || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                "& .MuiTablePagination-toolbar": {
                  paddingLeft: 2,
                  paddingRight: 2,
                },
              }}
            />
          </Paper>
        </Fade>
      )}

      {/* Mentor status action menu (Approve / Reject / Suspend / Reinstate) */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={handleCloseStatusMenu}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 180 } }}
      >
        {(
          mentorActionsByStatus[
            (statusMenuRow?.profile?.status || "PENDING").toUpperCase()
          ] ?? mentorActionsByStatus.PENDING
        ).map(({ action, label, color, Icon }) => (
          <MenuItem
            key={action}
            onClick={() => handleSelectMentorAction(statusMenuRow, action)}
            sx={{ "&:hover": { backgroundColor: `${color}14` } }}
          >
            <ListItemIcon>
              <Icon fontSize="small" sx={{ color }} />
            </ListItemIcon>
            <ListItemText
              primary={label}
              primaryTypographyProps={{ fontWeight: 600, color }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* Reason dialog — required for Reject / Suspend */}
      <Dialog
        open={Boolean(reasonDialogAction)}
        onClose={handleCloseReasonDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {reasonDialogAction === "SUSPEND"
            ? "Suspend Mentor"
            : "Reject Mentor"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {reasonDialogRow?.mentorName || reasonDialogRow?.profile?.fullName}{" "}
            will be marked as{" "}
            {reasonDialogAction === "SUSPEND" ? "Suspended" : "Rejected"}.
            Please provide a reason.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Reason"
            placeholder={
              reasonDialogAction === "SUSPEND"
                ? "Why is this mentor being suspended?"
                : "Why is this mentor being rejected?"
            }
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleCloseReasonDialog}>Cancel</Button>
          <Button
            variant="contained"
            color={reasonDialogAction === "SUSPEND" ? "secondary" : "error"}
            disabled={!reasonText.trim() || submittingReason}
            onClick={handleConfirmReasonDialog}
          >
            {submittingReason
              ? "Submitting..."
              : reasonDialogAction === "SUSPEND"
                ? "Suspend"
                : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bank Details View Dialog */}
      <Dialog
        open={openBankView}
        onClose={() => setOpenBankView(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 600,
          }}
        >
          Bank Details
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Add Bank Account" arrow>
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleAddBankAccount(bankViewRow)}
              >
                <AccountBalanceIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setOpenBankView(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {loadingBankAccounts ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : bankAccounts.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", py: 3 }}
            >
              No bank accounts added yet.
            </Typography>
          ) : (
            <Stack spacing={1.5} sx={{ py: 1 }}>
              {bankAccounts.map((account) => (
                <Box
                  key={account.id}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {account.accountHolder}
                    </Typography>
                    <Box
                      sx={{ display: "flex", gap: 0.5, alignItems: "center" }}
                    >
                      {account.isDefault && (
                        <Chip label="Default" size="small" color="primary" />
                      )}
                      {/* <Chip
                        label={account.verified ? "Verified" : "Unverified"}
                        size="small"
                        color={account.verified ? "success" : "default"}
                        variant="outlined"
                      /> */}
                      <Tooltip title="Edit" arrow>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditBankAccount(account)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete" arrow>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setBankAccountToDelete(account)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    {[
                      { label: "BANK NAME", value: account.bankName },
                      { label: "BRANCH", value: account.branch },
                      { label: "A/C NO", value: account.accountNumberMasked },
                      { label: "IFSC", value: account.ifsc },
                    ].map((f) => (
                      <Box
                        key={f.label}
                        sx={{
                          background: "#f5f7fb",
                          borderRadius: "8px",
                          px: 1.2,
                          py: 0.8,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.68rem",
                            color: "#8a94a6",
                            fontWeight: 600,
                          }}
                        >
                          {f.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            color: "#17479e",
                          }}
                        >
                          {f.value || "—"}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* Bank Details Add/Edit Modal */}
      <BankDetailsModal
        open={openBankEdit}
        onClose={() => setOpenBankEdit(false)}
        mentorProfileId={bankViewRow?.profile?.id}
        mentorId={bankViewRow?.profile?.mentorId}
        bankDetailsEdit={bankEditData}
        onSuccess={() => {
          setOpenBankEdit(false);
          fetchBankAccounts(bankViewRow?.profile?.id);
        }}
      />

      {/* Confirm Delete Bank Account Dialog */}
      <DeleteConfirmationDialog
        open={!!bankAccountToDelete}
        onClose={() => setBankAccountToDelete(null)}
        onConfirm={handleDeleteBankAccount}
        title="Confirm Delete"
        message="Are you sure you want to delete this bank account? This action cannot be undone."
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />

      {/* Banner modal */}
      <MentorDocumentsUploads
        open={openNew}
        onClose={handleNewClose}
        mentorshipDetailsEdit={mentorshipDetailsEdit}
        onSuccess={handleFormSuccess}
      />
      {/* Confirm Delete Dialog */}
      <DeleteConfirmationDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this news item? This action cannot be undone."
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
      <Dialog
        open={!!openImage}
        onClose={() => setOpenImage(null)}
        maxWidth="md"
      >
        <DialogContent>
          <Box position="relative">
            <IconButton
              aria-label="close"
              onClick={() => setOpenImage(null)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
            <img
              src={openImage}
              alt="Preview"
              style={{ width: "100%", height: "auto", borderRadius: 4 }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog
        open={!!openDoc}
        onClose={() => setOpenDoc(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 600,
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {openDoc && getDocIcon(openDoc.docType)}
            {openDoc?.docType
              ?.replace(/_/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase())}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              icon={
                isDocApproved(openDoc?.status) ? (
                  <CheckCircleIcon />
                ) : (
                  <CancelIcon />
                )
              }
              label={isDocApproved(openDoc?.status) ? "Verified" : "Rejected"}
              color={isDocApproved(openDoc?.status) ? "success" : "error"}
              size="small"
              variant="outlined"
            />
            <Button
              variant="contained"
              color={isDocApproved(openDoc?.status) ? "error" : "success"}
              size="small"
              startIcon={
                isDocApproved(openDoc?.status) ? (
                  <CancelIcon />
                ) : (
                  <CheckCircleIcon />
                )
              }
              onClick={handleApproveDoc}
              disabled={approvingDoc}
            >
              {approvingDoc
                ? "Saving..."
                : isDocApproved(openDoc?.status)
                  ? "Reject"
                  : "Approve"}
            </Button>
            <IconButton size="small" onClick={() => setOpenDoc(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {isDocApproved(openDoc?.status) && (
            <TextField
              fullWidth
              size="small"
              label="Review note (required to reject)"
              placeholder="Reason for rejecting this document..."
              value={docReviewNote}
              onChange={(e) => setDocReviewNote(e.target.value)}
              multiline
              minRows={2}
              sx={{ mb: 2 }}
            />
          )}
          {(() => {
            console.log(openDoc);

            const docUrl = openDoc ? resolveDocUrl(openDoc.filePath) : "";
            if (!docUrl) {
              return (
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  No document file available.
                </Typography>
              );
            }
            console.log("docUrl", docUrl);
            const isPdf = /\.pdf($|\?)/i.test(docUrl);
            return isPdf ? (
              <iframe
                src={`${docUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                title="Document"
                style={{
                  width: "100%",
                  height: "70vh",
                  border: "none",
                }}
              />
            ) : (
              <img
                src={docUrl}
                alt="Document"
                style={{ width: "100%", height: "auto", borderRadius: 4 }}
              />
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Demo Video Preview Dialog */}
      <Dialog
        open={!!openVideo}
        onClose={() => setOpenVideo(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 600,
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PlayCircleFilledIcon sx={{ color: "#17479e" }} />
            {openVideo?.subjectName}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              icon={
                openVideo?.status === "APPROVED" ? (
                  <CheckCircleIcon />
                ) : (
                  <CancelIcon />
                )
              }
              label={openVideo?.status === "APPROVED" ? "Approved" : "Rejected"}
              color={openVideo?.status === "APPROVED" ? "success" : "error"}
              size="small"
              variant="outlined"
            />
            <Button
              variant="contained"
              color={openVideo?.status === "APPROVED" ? "error" : "success"}
              size="small"
              startIcon={
                openVideo?.status === "APPROVED" ? (
                  <CancelIcon />
                ) : (
                  <CheckCircleIcon />
                )
              }
              onClick={handleApproveVideo}
              disabled={approvingVideo}
            >
              {approvingVideo
                ? "Saving..."
                : openVideo?.status === "APPROVED"
                  ? "Reject"
                  : "Approve"}
            </Button>
            <IconButton size="small" onClick={() => setOpenVideo(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {openVideo?.status === "APPROVED" && (
            <TextField
              fullWidth
              size="small"
              label="Review note (required to reject)"
              placeholder="Reason for rejecting this video..."
              value={videoReviewNote}
              onChange={(e) => setVideoReviewNote(e.target.value)}
              multiline
              minRows={2}
              sx={{ mb: 2 }}
            />
          )}
          {(() => {
            const videoUrl = openVideo ? resolveDocUrl(openVideo.videoUrl) : "";
            if (!videoUrl) {
              return (
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  No video file available.
                </Typography>
              );
            }
            return (
              <video
                src={videoUrl}
                controls
                autoPlay
                style={{
                  width: "100%",
                  maxHeight: "70vh",
                  borderRadius: 4,
                  backgroundColor: "#000",
                }}
              />
            );
          })()}
        </DialogContent>
      </Dialog>
      {/* Mentor configuration (Platform Fee / GST) */}
      <MentorConfigModal
        open={openConfig}
        onClose={() => setOpenConfig(false)}
      />
      {/* Edit Mentor Profile */}
      <MentorRegisterForm
        open={openRegisterForm}
        onClose={() => {
          setOpenRegisterForm(false);
          setRegisterFormMentor(null);
        }}
        mentorEdit={registerFormMentor}
        onSuccess={handleRegisterFormSuccess}
      />
    </>
  );
}

export default MentorsTable;
